/* eslint-disable class-methods-use-this */
// eslint-disable-next-line max-classes-per-file -- Legacy
import { shuffle, isNil, isFinite } from 'lodash'
import { AutocompleteErrorCodes } from '@commutifi-fe/commutifi-specific/Presentation/AddressAutocomplete'
import { Question, QuestionConfiguration, QUESTION_TYPES, ValidationRules } from '@commutifi-fe/interfaces'
import { QuestionType } from '@commutifi/models/Question'
import {
  Input,
  InputNumber,
  FormTimePicker as TimeSelector,
  FormTimePickerProps,
  AppInputNumberProps
} from '@commutifi-fe/ui'

import SecondaryCommute, { SecondaryCommuteRouterProps } from 'shared/components/common/CommuteRouter/SecondaryCommute'
import SelectableChoices, { QuestionOptions, SelectableChoicesProps } from 'shared/components/common/SelectableChoices'
import VehicleSelector, { IVehicleSelectorProps, validateVehicle } from 'shared/components/common/VehicleSelector'
import ActivityMapper, { IActivityMapperProps } from 'shared/components/common/ActivitySelector/ActivityMapper'
import { DayPicker, DayPickerProps, getDayPickerValidation } from 'shared/components/common/DayPicker'
import { CostSelector, ICost, ICostSelectorProps } from 'shared/components/common/CostSelector'
import CommuteRouter, { ICommuteRouter } from 'shared/components/common/CommuteRouter'
import SelectInput, { ISelectInputProps } from 'shared/components/common/SelectInput'
import ActivitySelector from 'shared/components/common/ActivitySelector'
import {
  CommuteTimelineDriveAndTransit,
  CommuteTimelineProps,
  EditableCommuteTimeline
} from 'shared/components/common/EditableCommuteTimeline'
import OfficeSelector, {
  SuggestOfficeSelectProps,
  validateOfficeDistance,
  validateOfficeOption
} from 'shared/components/common/SuggestOfficeSelect'
import AddressAutocompleteInput, {
  AddressAutocompleteInputProps
} from 'shared/components/common/AddressAutocompleteInput'
import SuggestCompanySelect, {
  SuggestCompanySelectProps,
  validateCompany
} from 'shared/components/common/SuggestCompanySelect'

import { DEFAULT_OFFICE_ARRIVAL_TIME_HHMM, DEFAULT_OFFICE_LEAVING_TIME_HHMM } from 'constants/global'
import { AddressGeocodeValue, DaysValueEnum, Frequency, MonToFri } from 'shared/interfaces/Commute'
import { getHomeGeolocation, getOfficeGeolocation, isQuestionBefore } from 'utils/helpers/surveys'
import { validateAddressDistance, validateAddressOption } from 'utils/helpers/address'
import { CumulatedAnswers, PrefilledData } from 'shared/interfaces/Survey'
import { FormattedMessage, IntlShape } from 'locales/index'
import { calculateAvailableFrequency } from 'utils/index'
import { OfficeGeocodeValue } from 'shared/interfaces'
import { REF_KEYS_LIST } from 'constants/onboarding'
import { COMPONENT_TYPES } from 'constants/surveys'
import React from 'react'

export interface SurveyQuestionConfig<T = any, S = any> {
  props: Partial<T>
  component: React.ComponentType<T> | React.ForwardRefExoticComponent<T>
  componentType: COMPONENT_TYPES
  defaultValue: S | undefined
}

// The interface that is used by the strategy
interface QuestionTypeStrategy<T = any> {
  /**
   * Output a react component, a type and props based on the type of the question.
   * Those are the supported inputs type for the survey.
   *
   * A component type='module' means we will handle it differently because the component
   * is composed of multiple questions
   *
   * @param question -                          Survey question containing, translation contains, type of question
   *                                                       question configuration and more
   *
   * @param prefilledData -                 Shortlink data that encapsulate the potential organization, enterprise,
   *                                                       building and account information linked to a specific survey
   *
   * @param cumulatedAnswers -           Cumulated survey question answers stored by the reference key
   *                                                       or the question id if there is no reference key
   *
   * @param questionsByRefKey - Survey question stored by the reference key
   *                                                       or the question id if there is no reference key
   */
  processFormProps: (
    question: Question,
    prefilledData?: PrefilledData,
    cumulatedAnswers?: CumulatedAnswers,
    questionsByRefKey?: Record<string, Question>
  ) => SurveyQuestionConfig

  /**
   * Get validation rules for the survey input component type passed
   * as an input parameter.
   * @param question -                   survey question containing, translation contains, type of question
   *                                                question configuration and more
   * @param cumulatedAnswers -     previous survey answers
   * @param props -                              specific component (from generic type 'T') props
   *
   * @returns antd getFieldDecorator options to validate field:
   * (rules, trigger, validateFirst, validateTrigger) : https://ant.design/components/form/
   */
  getFieldValidation: (question: Question, options: { props?: T; intl: IntlShape }) => ValidationRules

  /**
   * Display mode is used make some changes to the component to make it clearer what they can be used or
   * what they contain since they are non interactive in some cases like the survey builder.
   * @example - Options count in the dropdown question
   * @example - Display arrows that normally only show on hover in the InputNumber component
   * More use cases can be added
   */
  displayMode: boolean
  setDisplayMode: (mode: boolean) => void
}

const getDefaultValidation = (question: Question): ValidationRules => ({
  rules: [{ required: !question.optional, message: <FormattedMessage id="required" /> }]
})

class ActivityMapperStrategy implements QuestionTypeStrategy<IActivityMapperProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(
    _: Question,
    __: PrefilledData,
    cumulatedAnswers: CumulatedAnswers = {}
  ): SurveyQuestionConfig<IActivityMapperProps> {
    const remoteFrequency = cumulatedAnswers[REF_KEYS_LIST.commuteProfileRemoteFrequency]
    const workFrequency = cumulatedAnswers[REF_KEYS_LIST.commuteProfileFrequency]
    const commuteRoutes = cumulatedAnswers[REF_KEYS_LIST.commuteProfileRoute]
    const secondaryCommuteFrequency = commuteRoutes?.secondary?.frequency
    const primaryRoute = commuteRoutes?.primary?.route
    const secondaryRoute = commuteRoutes?.secondary?.route
    const primaryRouteFrequency = calculateAvailableFrequency(workFrequency, [
      remoteFrequency,
      secondaryCommuteFrequency
    ])
    return {
      component: ActivityMapper,
      componentType: COMPONENT_TYPES.Module,
      props: {
        primaryRoute,
        primaryRouteFrequency,
        secondaryRoute,
        secondaryRouteFrequency: secondaryCommuteFrequency
      },
      defaultValue: {
        stopLocations: [''],
        frequency: primaryRoute && !secondaryRoute ? primaryRouteFrequency : undefined
      }
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class ActivitySelectorStrategy implements QuestionTypeStrategy {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(): SurveyQuestionConfig {
    return {
      component: ActivitySelector,
      componentType: COMPONENT_TYPES.Module,
      props: {},
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class AddressAutocompleteStrategy implements QuestionTypeStrategy<AddressAutocompleteInputProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question): SurveyQuestionConfig<AddressAutocompleteInputProps> {
    const placeholder = question?.content?.placeholder
    return {
      component: AddressAutocompleteInput,
      componentType: COMPONENT_TYPES.Component,
      props: {
        placeholder,
        isInteractive: !this.displayMode
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question, { intl }: { props: AddressAutocompleteInputProps; intl: IntlShape }) {
    return {
      rules: [
        {
          validator: question.optional
            ? () => Promise.resolve()
            : (rule: any, value: AddressGeocodeValue) => validateAddressOption(rule, value, { intl })
        }
      ]
    }
  }
}

class CheckboxesStrategy implements QuestionTypeStrategy<SelectableChoicesProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question): SurveyQuestionConfig<SelectableChoicesProps> {
    const options = (question?.content?.options?.options || []) as QuestionOptions[] | null
    const config: QuestionConfiguration = question.configuration || {}

    return {
      component: SelectableChoices,
      componentType: COMPONENT_TYPES.Component,
      props: {
        options: config.randomizeOrder ? shuffle(options || []) : options || [],
        multiSelect: true
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question, { intl }: { intl: IntlShape }) {
    return {
      rules: [
        { required: !question.optional, message: <FormattedMessage id="required" /> },
        {
          validator: (_: any, value: string[] | undefined) => {
            if (question.optional && isNil(value)) {
              return Promise.resolve()
            }
            const config: QuestionConfiguration = question.configuration || {}
            if (config.minValue && !isNil(value) && value.length < config.minValue) {
              // eslint-disable-next-line prefer-promise-reject-errors -- Legacy
              return Promise.reject(
                <FormattedMessage
                  id="validation.multiChoice.min"
                  values={{ count: intl.formatNumber(config.minValue) }}
                />
              )
            }
            if (config.maxValue && !isNil(value) && value.length > config.maxValue) {
              // eslint-disable-next-line prefer-promise-reject-errors -- Legacy
              return Promise.reject(
                <FormattedMessage
                  id="validation.multiChoice.max"
                  values={{ count: intl.formatNumber(config.maxValue) }}
                />
              )
            }
            return Promise.resolve()
          }
        }
      ]
    }
  }
}

class CommuteRouterStrategy implements QuestionTypeStrategy<ICommuteRouter> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(
    _: Question,
    prefilledData?: PrefilledData,
    cumulatedAnswers?: CumulatedAnswers
  ): SurveyQuestionConfig<ICommuteRouter> {
    const officeGeo = getOfficeGeolocation(prefilledData, cumulatedAnswers)
    const homeGeo = getHomeGeolocation(cumulatedAnswers)
    const workFrequency = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileFrequency]
    const remoteFrequency = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileRemoteFrequency]
    const remainingFrequency = calculateAvailableFrequency(workFrequency, remoteFrequency).days

    return {
      component: CommuteRouter,
      componentType: COMPONENT_TYPES.Module,
      props: {
        startPoint: homeGeo,
        endPoint: officeGeo,
        arrivalTime: cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileArrive],
        // Spec states: When arriving on the Commute Router question, if days haven't been asked, remove question about 'secondary commute'.
        //              The remote work question should default to ask days (assuming days are Monday to Friday if not asked).
        //              So if I say remote Monday, then my CP will have Tuesday to Friday (even if not entered).
        // Also, we should not ask for a secondary commute if the user entered he works only one day
        askForSecondaryCommute: Boolean(
          this.displayMode ||
            (workFrequency?.days?.length && (remainingFrequency || []).length > 1) ||
            (!workFrequency?.days && remoteFrequency?.days?.length)
        ),
        showDropdownOptionsCount: this.displayMode
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class CommuteTimelineStrategy implements QuestionTypeStrategy<CommuteTimelineProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(
    question: Question,
    prefilledData?: PrefilledData,
    cumulatedAnswers?: CumulatedAnswers
  ): SurveyQuestionConfig<CommuteTimelineProps> {
    const officeGeo = getOfficeGeolocation(prefilledData, cumulatedAnswers)
    const homeGeo = getHomeGeolocation(cumulatedAnswers)
    return {
      component:
        QUESTION_TYPES.commuteTimeline === question.type ? EditableCommuteTimeline : CommuteTimelineDriveAndTransit,
      componentType: COMPONENT_TYPES.Component,
      props: {
        startPoint: homeGeo,
        endPoint: officeGeo,
        arrivalTime: cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileArrive]
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class CostSelectorStrategy implements QuestionTypeStrategy<ICostSelectorProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(): SurveyQuestionConfig<ICostSelectorProps> {
    return {
      component: CostSelector,
      componentType: COMPONENT_TYPES.Component,
      props: {},
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return {
      rules: [
        { required: !question.optional, message: <FormattedMessage id="required" /> },
        {
          validator: (rule: any, value: ICost) => {
            if (value?.cost && value.cost < 0) {
              // eslint-disable-next-line prefer-promise-reject-errors -- Legacy
              return Promise.reject(<FormattedMessage id="onboarding.costSelector.tooLow" />)
            }
            return Promise.resolve()
          }
        }
      ]
    }
  }
}

class DayPickerStrategy implements QuestionTypeStrategy<DayPickerProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(
    question: Question,
    _?: PrefilledData,
    cumulatedAnswers?: CumulatedAnswers,
    questionsByRefKey?: Record<string, Question>
  ): SurveyQuestionConfig<DayPickerProps, Frequency> {
    const config: QuestionConfiguration = question.configuration || {}

    const remoteFrequencyQuestion = questionsByRefKey?.[REF_KEYS_LIST.commuteProfileRemoteFrequency] || ({} as Question)
    const remoteFrequencyAnswer = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileRemoteFrequency]

    const workFrequencyQuestion = questionsByRefKey?.[REF_KEYS_LIST.commuteProfileFrequency] || ({} as Question)
    const workFrequencyAnswer = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileFrequency]

    const secondaryCommuteFrequency = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileRoute]?.secondary?.frequency

    const commuteRoutesQuestion = questionsByRefKey?.[REF_KEYS_LIST.commuteProfileRoute] || ({} as Question)
    const commuteRoutesAnswer = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileRoute] || ({} as CumulatedAnswers)

    const dayPickerProps: Partial<DayPickerProps> = {}
    let dayPickerDefaultValue: Frequency | undefined
    // Restrict the profile remote frequency questions to consider the work frequency to limit
    // the users to the days (or maximum weekly frequency) he selected in commute profile frequency question
    if (question.referenceKey === REF_KEYS_LIST.commuteProfileRemoteFrequency) {
      const secondaryIsBefore =
        commuteRoutesQuestion.section < question.section && commuteRoutesAnswer?.secondary?.route
      const availableFrequency = calculateAvailableFrequency(
        workFrequencyAnswer,
        // Only restrict remote frequency with the secondary commute frequency if
        // the secondary commute frequency question was before
        secondaryIsBefore ? secondaryCommuteFrequency : undefined,
        secondaryIsBefore ? 1 : 0
      )

      // The remote work question should default to ask days (assuming days are Monday to Friday if not asked).
      // So if I say remote Monday, then my CP will have Tuesday to Friday (even if not entered)
      // !!! if you change the setDays default value to be false and the days subset, you will want to take
      //     a look to the configuration of REF_KEYS_LIST.commuteProfileFrequency right under
      dayPickerProps.allowFrequency = false
      dayPickerProps.setDays = true
      dayPickerProps.daysSubset = availableFrequency.days || MonToFri
      dayPickerProps.maxWeeklyFrequency = availableFrequency.weeklyFrequency || MonToFri.length
    } else if (question.referenceKey === REF_KEYS_LIST.commuteProfileFrequency) {
      const previousCumulatedFrequencies: Frequency[] = []
      const hasRemoteFrequency =
        Boolean(!workFrequencyQuestion.section && remoteFrequencyQuestion.section) ||
        isQuestionBefore(remoteFrequencyQuestion, workFrequencyQuestion)

      if (hasRemoteFrequency && remoteFrequencyAnswer) {
        previousCumulatedFrequencies.push(remoteFrequencyAnswer)
      }

      const isCommuteRouterBefore = isQuestionBefore(commuteRoutesQuestion, question)
      const hasSecondaryCommuteFrequency = Boolean(commuteRoutesAnswer?.secondary?.route && isCommuteRouterBefore)
      if (hasSecondaryCommuteFrequency && secondaryCommuteFrequency) {
        previousCumulatedFrequencies.push(secondaryCommuteFrequency)
      }

      // When the commute profile frequency question is after any other frequency question we want to
      // restrict the user to pick AT LEAST the same number of days of the cumulated frequencies.
      // Also, we force to be 'No set days' as this is the default behavior when we need to assume the
      // commute frequency in either the remote frequency question or secondary commute question
      if (previousCumulatedFrequencies.length > 0) {
        const minimumWeeklyFrequency = previousCumulatedFrequencies.reduce(
          (cumulatedFrequencyObj, frequency) => cumulatedFrequencyObj + frequency.weeklyFrequency,
          isCommuteRouterBefore ? 1 : 0
        )

        // If the user already answered a remote frequency we need to default mon to fri
        // since it is the default configuration for the remote days question right now.
        dayPickerProps.setDays = true
        dayPickerProps.allowFrequency = false
        dayPickerProps.minWeeklyFrequency = minimumWeeklyFrequency
        dayPickerProps.disabledOptions = (remoteFrequencyAnswer?.days || [])
          .concat(secondaryCommuteFrequency?.days || [])
          .filter((day) => !isNil(day))
        dayPickerDefaultValue = {
          days: MonToFri,
          weeklyFrequency: 5
        }
      } else {
        dayPickerDefaultValue = {
          days: MonToFri,
          weeklyFrequency: 5
        }
      }
    }

    if (this.displayMode) {
      dayPickerProps.allowFrequency = false
      dayPickerProps.setDays = true
    }

    return {
      component: DayPicker,
      componentType: COMPONENT_TYPES.Component,
      props: {
        allowFrequency: config.allowFrequency,
        ...dayPickerProps
      },
      defaultValue: dayPickerDefaultValue
    }
  }

  getFieldValidation(question: Question, { props }: { props: DayPickerProps; intl: IntlShape }) {
    return getDayPickerValidation(props.minWeeklyFrequency, props.maxWeeklyFrequency, !question.optional)
  }
}

class DropdownStrategy implements QuestionTypeStrategy<ISelectInputProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question): SurveyQuestionConfig<ISelectInputProps> {
    const options = (question?.content?.options?.options || []) as QuestionOptions[]
    return {
      component: SelectInput,
      componentType: COMPONENT_TYPES.Component,
      props: {
        options,
        placeholder: question?.content?.placeholder,
        showDropdownOptionsCount: this.displayMode
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class EnterpriseSelectorStrategy implements QuestionTypeStrategy<SuggestCompanySelectProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question, prefilledData?: PrefilledData): SurveyQuestionConfig<SuggestCompanySelectProps> {
    const preselectEnterprise: { id: string; name?: string } | undefined = prefilledData?.enterprise
      ? prefilledData.enterprise
      : prefilledData?.accountContext?.enterpriseId
        ? { id: prefilledData.accountContext?.enterpriseId }
        : undefined

    const config: QuestionConfiguration = question.configuration || {}
    const placeholder = question?.content?.placeholder || undefined
    return {
      component: SuggestCompanySelect,
      componentType: COMPONENT_TYPES.Component,
      props: {
        placeholder,
        allowCreation: config.allowCreation,
        organizationId: prefilledData?.organization?.id || (prefilledData?.accountContext?.organizationId ?? undefined)
      },
      defaultValue: preselectEnterprise
        ? { key: preselectEnterprise.id, label: preselectEnterprise.name || preselectEnterprise.id }
        : undefined
    }
  }

  getFieldValidation() {
    return {
      validateTrigger: 'onSelect',
      rules: [{ validator: validateCompany }]
    }
  }
}

type InputProps = React.ComponentProps<typeof Input>
class InputStrategy implements QuestionTypeStrategy<InputProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question): SurveyQuestionConfig<InputProps> {
    const placeholder = question?.content?.placeholder || undefined
    return {
      component: Input,
      componentType: COMPONENT_TYPES.Component,
      props: { placeholder },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return {
      rules: [
        { required: !question.optional, message: <FormattedMessage id="required" /> },
        {
          validator: (rule: any, value: string | null | undefined) => {
            // Add a max length of 500 characters
            if (value && value.length > 500) {
              // eslint-disable-next-line prefer-promise-reject-errors -- Legacy
              return Promise.reject(<FormattedMessage id="onboarding.textInput.invalid" />)
            }
            return Promise.resolve()
          }
        }
      ]
    }
  }
}

class HomeAddressAutocompleteStrategy implements QuestionTypeStrategy<AddressAutocompleteInputProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(
    question: Question,
    prefilledData?: PrefilledData,
    cumulatedAnswers?: CumulatedAnswers
  ): SurveyQuestionConfig<AddressAutocompleteInputProps> {
    const officeGeo = getOfficeGeolocation(prefilledData, cumulatedAnswers)
    const placeholder = question?.content?.placeholder || undefined
    return {
      component: AddressAutocompleteInput,
      componentType: COMPONENT_TYPES.Component,
      props: { placeholder, locationBias: officeGeo?.location },
      defaultValue: prefilledData?.accountContext
        ? {
            value: prefilledData.accountContext?.address,
            formattedAddress: prefilledData.accountContext?.address,
            location: prefilledData.accountContext?.location,
            isInteractive: !this.displayMode
          }
        : undefined
    }
  }

  getFieldValidation(question: Question, { props, intl }: { props: AddressAutocompleteInputProps; intl: IntlShape }) {
    return {
      rules: [
        {
          validator: (rule: any, value: AddressGeocodeValue | AutocompleteErrorCodes) =>
            validateAddressOption(rule, value, { intl })
        },
        {
          warningOnly: true,
          validator: (rule: any, value: AddressGeocodeValue | AutocompleteErrorCodes) => {
            if (!question.referenceKey || question.referenceKey !== REF_KEYS_LIST.accountAddress) {
              return Promise.resolve()
            }
            return validateAddressDistance(rule, value, { officeLocation: props.locationBias, intl })
          }
        }
      ]
    }
  }
}

class NumberInputStrategy implements QuestionTypeStrategy<AppInputNumberProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question): SurveyQuestionConfig<AppInputNumberProps> {
    const placeholder = question?.content?.placeholder || undefined
    const config: QuestionConfiguration = question.configuration || {}
    const max = config.maxValue
    const min = config.minValue
    return {
      component: InputNumber,
      componentType: COMPONENT_TYPES.Component,
      props: {
        placeholder,
        style: { width: '100%' },
        alwaysShowControls: this.displayMode,
        ...(max && { max }),
        ...(min && { min })
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    // Give access to the answers in the form so we can get other answered questions value
    return {
      rules: [
        { required: !question.optional, message: <FormattedMessage id="required" /> },
        {
          validator: (rule: any, value: number | null | undefined) => {
            // If the value is required we check if it is a valid finite number
            if (value && !isFinite(value) && !isNil(value)) {
              // eslint-disable-next-line prefer-promise-reject-errors -- Legacy
              return Promise.reject(<FormattedMessage id="onboarding.numberInput.invalid" />)
            }
            return Promise.resolve()
          }
        }
      ]
    }
  }
}

class OfficeSelectorStrategy implements QuestionTypeStrategy<SuggestOfficeSelectProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(
    question: Question,
    prefilledData?: PrefilledData,
    cumulatedAnswers?: CumulatedAnswers
  ): SurveyQuestionConfig<SuggestOfficeSelectProps> {
    const officeGeo = getOfficeGeolocation(prefilledData, cumulatedAnswers)
    const homeGeo = getHomeGeolocation(cumulatedAnswers)
    const placeholder = question?.content?.placeholder || undefined
    const config: QuestionConfiguration = question.configuration || {}

    return {
      component: OfficeSelector,
      componentType: COMPONENT_TYPES.Component,
      props: {
        placeholder,
        allowCreation: config.allowCreation,
        locationBias: homeGeo?.location,
        enterpriseId:
          prefilledData?.enterprise?.id ||
          prefilledData?.accountContext?.enterpriseId ||
          cumulatedAnswers?.[REF_KEYS_LIST.accountEnterpriseId]?.key,
        isInteractive: !this.displayMode
      },
      defaultValue: officeGeo
    }
  }

  getFieldValidation(question: Question, { props }: { props: SuggestOfficeSelectProps; intl: IntlShape }) {
    // Give access to the answers in the form so we can get other answered questions value
    return {
      validateTrigger: 'onSelect',
      rules: [
        {
          validator: (rule: any, options: string | OfficeGeocodeValue) => validateOfficeOption(rule, options)
        },
        {
          warningOnly: true,
          validator: (rule: any, options: string | OfficeGeocodeValue) => {
            if (!question.referenceKey || question.referenceKey !== REF_KEYS_LIST.accountOfficeId) {
              return Promise.resolve()
            }
            return validateOfficeDistance(rule, options, props.locationBias)
          }
        }
      ]
    }
  }
}

class RadioButtonStrategy implements QuestionTypeStrategy<SelectableChoicesProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question): SurveyQuestionConfig<SelectableChoicesProps> {
    const options = (question?.content?.options?.options || []) as QuestionOptions[] | null
    return {
      component: SelectableChoices,
      componentType: COMPONENT_TYPES.Component,
      props: {
        options: options || []
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class SecondaryCommuteRouterStrategy implements QuestionTypeStrategy<SecondaryCommuteRouterProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(
    question: Question,
    prefilledData?: PrefilledData,
    cumulatedAnswers?: CumulatedAnswers,
    questionsByRefKey?: Record<string, Question>
  ): SurveyQuestionConfig<SecondaryCommuteRouterProps> {
    const commuteFrequency = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileFrequency]
    const remoteFrequencyQuestion = questionsByRefKey?.[REF_KEYS_LIST.commuteProfileRemoteFrequency] || ({} as Question)
    const remoteFrequencyAnswer = cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileRemoteFrequency]
    const officeGeo = getOfficeGeolocation(prefilledData, cumulatedAnswers)
    const homeGeo = getHomeGeolocation(cumulatedAnswers)
    return {
      component: SecondaryCommute,
      componentType: COMPONENT_TYPES.Module,
      props: {
        startPoint: homeGeo,
        endPoint: officeGeo,
        arrivalTime: cumulatedAnswers?.[REF_KEYS_LIST.commuteProfileArrive],
        availableFrequency: calculateAvailableFrequency(
          // If the primary commute frequency is not set yet we will assume that we can select any days (7 days available)
          // only if the user didn't pick specific days for his remote work (in that case the user SHOULD pick specific days)
          !commuteFrequency && remoteFrequencyAnswer?.days
            ? { days: [...MonToFri, DaysValueEnum.Saturday, DaysValueEnum.Sunday], weeklyFrequency: 7 }
            : commuteFrequency,
          // Only restrict secondary frequency with the remote frequency if the remote frequency question was before
          remoteFrequencyQuestion.section || Number.NEGATIVE_INFINITY < question.section
            ? remoteFrequencyAnswer
            : undefined,
          1
        )
      },
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class TimeSelectorStrategy implements QuestionTypeStrategy<FormTimePickerProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(question: Question): SurveyQuestionConfig<FormTimePickerProps> {
    // Set default value for time picker to 9am and 5pm
    const defaultValue =
      question.referenceKey === REF_KEYS_LIST.commuteProfileArrive
        ? DEFAULT_OFFICE_ARRIVAL_TIME_HHMM
        : question.referenceKey === REF_KEYS_LIST.commuteProfileLeave
          ? DEFAULT_OFFICE_LEAVING_TIME_HHMM
          : undefined
    const placeholder = question?.content?.placeholder || undefined

    return {
      component: TimeSelector,
      componentType: COMPONENT_TYPES.Component,
      props: { placeholder, allowClear: false },
      defaultValue
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

class VehicleSelectorStrategy implements QuestionTypeStrategy<IVehicleSelectorProps> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(): SurveyQuestionConfig<IVehicleSelectorProps> {
    return {
      component: VehicleSelector,
      componentType: COMPONENT_TYPES.Component,
      props: { isInteractive: !this.displayMode },
      defaultValue: undefined
    }
  }

  getFieldValidation() {
    return {
      rules: [
        {
          validator: validateVehicle
        }
      ]
    }
  }
}

class StatementStrategy implements QuestionTypeStrategy<Record<string, never>> {
  displayMode = false

  setDisplayMode(mode: boolean) {
    this.displayMode = mode
  }

  processFormProps(): SurveyQuestionConfig<Record<string, never>> {
    function Component() {
      return <span />
    }
    return {
      component: Component,
      componentType: COMPONENT_TYPES.Component,
      props: {},
      defaultValue: undefined
    }
  }

  getFieldValidation(question: Question) {
    return getDefaultValidation(question)
  }
}

type Class<I, Args extends any[] = any[]> = new (...args: Args) => I
type QuestionTypeEnumValue = (typeof QUESTION_TYPES)[keyof typeof QUESTION_TYPES]
// Select and apply the correct strategy
const QuestionTypeToClass: Record<QuestionTypeEnumValue, Class<QuestionTypeStrategy>> = {
  [QUESTION_TYPES.activityMapper]: ActivityMapperStrategy,
  [QUESTION_TYPES.activitySelector]: ActivitySelectorStrategy,
  [QUESTION_TYPES.addressAutocomplete]: AddressAutocompleteStrategy,
  [QUESTION_TYPES.checkboxes]: CheckboxesStrategy,
  [QUESTION_TYPES.commuteRouter]: CommuteRouterStrategy,
  [QUESTION_TYPES.commuteTimeline]: CommuteTimelineStrategy,
  [QUESTION_TYPES.commuteTimelineDriveAndTransit]: CommuteTimelineStrategy,
  [QUESTION_TYPES.costSelector]: CostSelectorStrategy,
  [QUESTION_TYPES.daySelector]: DayPickerStrategy,
  [QUESTION_TYPES.dropdown]: DropdownStrategy,
  [QUESTION_TYPES.enterpriseSelector]: EnterpriseSelectorStrategy,
  [QUESTION_TYPES.homeAddressAutocomplete]: HomeAddressAutocompleteStrategy,
  [QUESTION_TYPES.numberInput]: NumberInputStrategy,
  [QUESTION_TYPES.officeSelector]: OfficeSelectorStrategy,
  [QUESTION_TYPES.radioButton]: RadioButtonStrategy,
  [QUESTION_TYPES.secondaryCommuteRouter]: SecondaryCommuteRouterStrategy,
  [QUESTION_TYPES.textInput]: InputStrategy,
  [QUESTION_TYPES.timeSelector]: TimeSelectorStrategy,
  [QUESTION_TYPES.vehicleSelector]: VehicleSelectorStrategy,
  [QUESTION_TYPES.statement]: StatementStrategy,
  // Not supported yet
  [QUESTION_TYPES.gridQuestions]: CheckboxesStrategy
}

export const QuestionConfigStrategyFactory = {
  getQuestionTypeStrategy: (questionType: string | QuestionType): QuestionTypeStrategy =>
    new QuestionTypeToClass[questionType || QUESTION_TYPES.textInput]()
}
